import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, collection, query, where, onSnapshot, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useProfile } from '../../context/ProfileContext';
import PlaylistArtwork from './PlaylistArtwork';
import toast from 'react-hot-toast';

const AddToPlaylistModal = ({ isOpen, onClose, songToAdd, onNavigateToCreate }) => {
  const [playlists, setPlaylists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const { activeProfile, isDarkMode } = useProfile();

  // Effect to fetch the user's playlists when the modal opens
  useEffect(() => {
    if (!isOpen || !activeProfile?.id) {
      return;
    }

    setIsLoading(true);
    const db = getFirestore();
    const q = query(collection(db, 'playlists'), where('ownerId', '==', activeProfile.id));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const playlistData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPlaylists(playlistData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [isOpen, activeProfile?.id]);

  const handleSelectPlaylist = async (playlist) => {
    if (isUpdating || !songToAdd?.id) return;
    
    // Check if the song is already inside this playlist's songIds array
    const songIds = playlist.songIds || [];
    const isAlreadyAdded = songIds.includes(songToAdd.id);

    if (isAlreadyAdded) {
      toast.error(`"${songToAdd.title}" is already in ${playlist.name}!`);
      return;
    }
    
    setIsUpdating(true);
    const db = getFirestore();
    const playlistRef = doc(db, 'playlists', playlist.id);

    try {
      // arrayUnion prevents duplicate entries
      await updateDoc(playlistRef, {
        songIds: arrayUnion(songToAdd.id)
      });
      toast.success(`Added to ${playlist.name}`);
      onClose(); // Close the modal on success
    } catch (error) {
      console.error("Error adding song to playlist:", error);
      toast.error("Failed to add song. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={`rounded-[2rem] shadow-2xl w-full max-w-sm flex flex-col overflow-hidden transition-all duration-300 border ${
              isDarkMode 
                ? "bg-[#1a1a1a] border-white/10" 
                : "bg-white border-slate-100"
            }`}
            style={{ height: '70vh', maxHeight: '500px' }}
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✅ RESTORED: Original text-xl font-bold p-4 border-b Title */}
            <h2 className="text-xl font-bold p-4 border-b">Add to Playlist</h2>
            
            <div className="flex-grow overflow-y-auto">
              {isLoading ? (
                <p className="text-center p-4">Loading playlists...</p>
              ) : (
                <ul>
                  {/* "Create New" Button (Kept original padding and structure) */}
                  <li 
                    className="flex items-center p-4 cursor-pointer rounded-2xl transition-colors hover:bg-gray-100 dark:hover:bg-white/5 group"
                    onClick={onNavigateToCreate}
                  >
                    <div className="w-12 h-12 bg-gray-200 dark:bg-[#ff00cc]/10 rounded-xl flex items-center justify-center mr-4 group-hover:scale-105 transition-transform">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6 text-slate-600 dark:text-[#ff00cc]">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                    </div>
                    <p className="font-semibold">Create New Playlist</p>
                  </li>

                  {/* List of existing playlists */}
                  {playlists.map(playlist => {
                    const songIds = playlist.songIds || [];
                    const isAlreadyAdded = songIds.includes(songToAdd?.id);

                    return (
                      <li 
                        key={playlist.id}
                        className={`flex items-center justify-between p-4 cursor-pointer rounded-2xl transition-colors ${
                          // ✅ FIXED: Non-blinding dark mode hover, native light mode hover
                          isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-100"
                        }`}
                        onClick={() => handleSelectPlaylist(playlist)}
                      >
                        <div className="flex items-center min-w-0">
                          <div className="w-12 h-12 mr-4 flex-shrink-0">
                            <PlaylistArtwork playlist={playlist} />
                          </div>
                          
                          {/* ✅ RESTORED: Original font-semibold name text style */}
                          <p className={`font-semibold truncate ${
                            isDarkMode ? "text-zinc-100" : "text-slate-850"
                          }`}>
                            {playlist.name}
                          </p>
                        </div>

                        {/* ✅ Pink Circle with a Black Tick */}
                        {isAlreadyAdded && (
                          <div className="flex-shrink-0 ml-2">
                            <div className="w-5.5 h-5.5 rounded-full bg-brand-pink flex items-center justify-center shadow-lg shadow-brand-pink/20 animate-fade-in">
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                className="h-3 w-3 text-black" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor" 
                                strokeWidth={4}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* ✅ RESTORED: Original p-6 padding with large, highly accessible Close Button */}
            <div className="p-6 border-t dark:border-white/5">
              <button
                onClick={onClose}
                type="button"
                className="w-full px-4 py-2 rounded-md text-gray-700 font-semibold bg-gray-200 hover:bg-gray-300 dark:bg-white/10 text-slate-500 dark:text-brand-white hover:bg-gray-200 dark:hover:bg-white/10 active:scale-95"
              >
                Close
              </button>
            </div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddToPlaylistModal;